import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useNotificationContext } from '../contexts/NotificationContext';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import useCascadeDelete from '../hooks/useCascadeDelete';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const Classes = () => {
  const { showSuccess, showError } = useNotificationContext();
  const { deleteClass, loading: deleteLoading } = useCascadeDelete();
  const [classes, setClasses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [departments, setDepartments] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [filteredTracks, setFilteredTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, classId: null, className: '' });
  const [filters, setFilters] = useState({
    department: '',
    academicYear: '',
    track: ''
  });
  const [formData, setFormData] = useState({
    name: '',
    level: 1,
    track: '',
    department: '',
    academicYear: '',
    students: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchClasses();
  }, [filters]);

  // Update filtered tracks when department or academic year filter changes
  useEffect(() => {
    if ((filters.department || filters.academicYear) && tracks.length > 0) {
      const deptTracks = tracks.filter(track => {
        const deptMatch = !filters.department || 
          track.department === filters.department || 
          track.department?._id === filters.department;
        const yearMatch = !filters.academicYear || 
          track.academicYear === filters.academicYear || 
          track.academicYear?._id === filters.academicYear;
        return deptMatch && yearMatch;
      });
      setFilteredTracks(deptTracks);
      // Reset track filter if current selection is not in filtered list
      if (filters.track && !deptTracks.some(t => t._id === filters.track)) {
        setFilters(prev => ({ ...prev, track: '' }));
      }
    } else {
      setFilteredTracks(tracks);
    }
  }, [filters.department, filters.academicYear, tracks]);

  // WebSocket message handler
  const handleWebSocketMessage = useCallback((data) => {
    console.log('📨 Classes.js handleWebSocketMessage called with:', data);
    
    if (data.type === 'connection') {
      console.log('🔗 Connection message:', data.message);
    } else if (data.type === 'class_updated') {
      console.log('🔄 Class update reçu:', data.data?.action, data.data);
      
      if (data.data?.action === 'update' && data.data?.class) {
        // Mise à jour directe de l'état local avec les nouvelles données
        console.log('🔄 Updating class in local state:', data.data.class._id);
        setClasses(prev => prev.map(cls => 
          cls._id === data.data.class._id ? data.data.class : cls
        ));
      } else if (data.data?.action === 'delete' && data.data?.classId) {
        // Suppression directe de l'état local
        console.log('🗑️ Removing class from local state:', data.data.classId);
        setClasses(prev => prev.filter(cls => cls._id !== data.data.classId));
      } else if (data.data?.action === 'create' && data.data?.class) {
        // Ajout direct à l'état local seulement si pas déjà présente
        console.log('➕ Adding class to local state:', data.data.class._id);
        setClasses(prev => {
          const exists = prev.some(cls => cls._id === data.data.class._id);
          if (exists) {
            console.log('⚠️ Class already exists, skipping add');
            return prev;
          }
          return [...prev, data.data.class];
        });
      } else {
        console.log('🔄 Refreshing classes list...');
        fetchClasses();
      }
    } else {
      console.log('❓ Unknown message type:', data.type);
    }
  }, []);

  // Use WebSocket context
  const { isConnected } = useWebSocket('classes', handleWebSocketMessage);

  const fetchData = async () => {
    try {
      const [classesRes, departmentsRes, academicYearsRes, tracksRes] = await Promise.all([
        axios.get(`${API_URL}/api/classes?limit=1000`),
        axios.get(`${API_URL}/api/departments?limit=1000`),
        axios.get(`${API_URL}/api/academic-years?limit=1000`),
        axios.get(`${API_URL}/api/tracks?limit=1000`)
      ]);
      
      setClasses(Array.isArray(classesRes.data) ? classesRes.data : classesRes.data.data || []);
      setDepartments(Array.isArray(departmentsRes.data) ? departmentsRes.data : departmentsRes.data.data || []);
      setAcademicYears(Array.isArray(academicYearsRes.data) ? academicYearsRes.data : academicYearsRes.data.data || []);
      setTracks(Array.isArray(tracksRes.data) ? tracksRes.data : tracksRes.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      console.log('🔄 Fetching classes...');
      let url = `${API_URL}/api/classes`;
      const params = new URLSearchParams();
      
      if (filters.department) params.append('department', filters.department);
      if (filters.academicYear) params.append('academicYear', filters.academicYear);
      if (filters.track) params.append('track', filters.track);
      params.append('limit', '10000'); // Pas de pagination
      params.append('nocache', 'true'); // Forcer le bypass du cache
      params.append('_t', Date.now()); // Timestamp pour éviter le cache navigateur
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        }
      });
      const classesData = Array.isArray(response.data) ? response.data : response.data.data || [];
      console.log('✅ Classes fetched:', classesData.length, 'classes');
      setClasses(classesData);
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editingClass) {
        console.log('📝 Updating class:', editingClass._id);
        response = await axios.put(`${API_URL}/api/classes/${editingClass._id}`, formData);
        console.log('✅ Class updated:', response.data);
        // Ne pas mettre à jour l'état local, laisser le WebSocket le faire
      } else {
        console.log('➕ Creating new class');
        response = await axios.post(`${API_URL}/api/classes`, formData);
        console.log('✅ Class created:', response.data);
        // Ne pas mettre à jour l'état local, laisser le WebSocket le faire
      }
      closeModal();
      console.log('🕰️ Waiting for WebSocket confirmation...');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      // En cas d'erreur, rafraîchir pour restaurer l'état correct
      fetchClasses();
    }
  };

  const confirmDelete = (classItem) => {
    setDeleteConfirm({ 
      show: true, 
      classId: classItem._id, 
      className: classItem.name 
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteConfirm.classId) {
      // Suppression immédiate de l'état local
      setClasses(prev => prev.filter(cls => cls._id !== deleteConfirm.classId));
      await deleteClass(deleteConfirm.classId);
    }
    setDeleteConfirm({ show: false, classId: null, className: '' });
  };

  const cancelDelete = () => {
    setDeleteConfirm({ show: false, classId: null, className: '' });
  };

  const openModal = async (classItem = null) => {
    setEditingClass(classItem);
    
    if (classItem) {
      // Mode édition
      const departmentId = classItem.department?._id || classItem.department;
      const academicYearId = classItem.academicYear?._id || classItem.academicYear;
      setFormData({
        name: classItem.name,
        level: classItem.level,
        track: classItem.track?._id || classItem.track || '',
        department: departmentId,
        academicYear: academicYearId,
        students: classItem.students
      });
      
      // Charger les parcours pour le département et l'année académique de la classe
      if (departmentId) {
        try {
          let url = `${API_URL}/api/tracks?department=${departmentId}&limit=1000`;
          if (academicYearId) {
            url += `&academicYear=${academicYearId}`;
          }
          const response = await axios.get(url);
          setAvailableTracks(Array.isArray(response.data) ? response.data : response.data.data || []);
        } catch (error) {
          console.error('Erreur lors du chargement des parcours:', error);
          setAvailableTracks([]);
        }
      }
    } else {
      // Mode ajout
      const defaultDeptId = departments.length > 0 ? departments[0]._id : '';
      const defaultYearId = academicYears.length > 0 ? academicYears[0]._id : '';
      setFormData({
        name: '',
        level: 1,
        track: '',
        department: defaultDeptId,
        academicYear: defaultYearId,
        students: 0
      });
      
      // Charger les parcours pour le premier département et première année
      if (defaultDeptId) {
        try {
          let url = `${API_URL}/api/tracks?department=${defaultDeptId}&limit=1000`;
          if (defaultYearId) {
            url += `&academicYear=${defaultYearId}`;
          }
          const response = await axios.get(url);
          setAvailableTracks(Array.isArray(response.data) ? response.data : response.data.data || []);
        } catch (error) {
          console.error('Erreur lors du chargement des parcours:', error);
          setAvailableTracks([]);
        }
      }
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingClass(null);
  };

  const handleChange = async (e) => {
    const { name, value } = e.target;
    const newValue = name === 'level' || name === 'students' ? Number(value) : value;
    
    // Mise à jour immédiate du formData
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));
    
    // Si le département ou l'année académique change, charger les parcours correspondants
    if ((name === 'department' || name === 'academicYear') && value) {
      try {
        const deptId = name === 'department' ? value : formData.department;
        const yearId = name === 'academicYear' ? value : formData.academicYear;
        
        let url = `${API_URL}/api/tracks?limit=1000`;
        if (deptId) url += `&department=${deptId}`;
        if (yearId) url += `&academicYear=${yearId}`;
        
        const response = await axios.get(url);
        const tracksData = Array.isArray(response.data) ? response.data : response.data.data || [];
        setAvailableTracks(tracksData);
        
        // Réinitialiser le parcours sélectionné seulement si nécessaire
        setFormData(prev => ({
          ...prev,
          track: tracksData.length > 0 ? tracksData[0]._id : ''
        }));
      } catch (error) {
        console.error('Erreur lors du chargement des parcours:', error);
        setAvailableTracks([]);
      }
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const getLevelName = (level) => {
    switch (level) {
      case 1: return '1ère année';
      case 2: return '2ème année';
      case 3: return '3ème année';
      default: return `Niveau ${level}`;
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredClasses = classes.filter(cls => {
    let match = true;
    
    // Filtres existants
    if (filters.department && cls.department) {
      match = match && (cls.department._id === filters.department || cls.department === filters.department);
    }
    
    if (filters.academicYear && cls.academicYear) {
      match = match && (cls.academicYear._id === filters.academicYear || cls.academicYear === filters.academicYear);
    }

    if (filters.track && cls.track) {
      match = match && (cls.track._id === filters.track || cls.track === filters.track);
    }
    
    // Filtre de recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      match = match && (
        cls.name.toLowerCase().includes(term) ||
        cls.track?.name.toLowerCase().includes(term) ||
        cls.department?.name.toLowerCase().includes(term) ||
cls.academicYear?.name.toLowerCase().includes(term)
      );
    }
    
    return match;
  });

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-2"></div>
        <p className="text-sm text-gray-600">Chargement...</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-0">
            <h1 className="text-xl font-semibold text-gray-900">Classes</h1>
            <span className={`px-2 py-1 text-xs rounded-full ${
              isConnected 
                ? 'bg-green-100 text-green-700' 
                : 'bg-red-100 text-red-700'
            }`}>
              {isConnected ? '🟢 Live' : '🔴 Manual'}
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={handleSearch}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <button 
              onClick={() => openModal()} 
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
            >
              + Ajouter
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 mb-6">
          <select 
            name="department" 
            value={filters.department} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Tous départements</option>
            {departments.map(dept => (
              <option key={dept._id} value={dept._id}>{dept.name}</option>
            ))}
          </select>
          <select 
            name="academicYear" 
            value={filters.academicYear} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Toutes années</option>
            {academicYears.map(year => (
              <option key={year._id} value={year._id}>{year.name}</option>
            ))}
          </select>
          <select 
            name="track" 
            value={filters.track} 
            onChange={handleFilterChange}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={!filters.department && !filters.academicYear}
          >
            <option value="">Toutes filières</option>
            {filteredTracks.map(track => (
              <option key={track._id} value={track._id}>{track.name}</option>
            ))}
          </select>
          {(filters.department || filters.academicYear || filters.track) && (
            <button 
              onClick={() => setFilters({ department: '', academicYear: '', track: '' })}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Réinitialiser
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredClasses.map(cls => (
            <div key={cls._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-medium text-gray-900 text-sm">{cls.name}</h3>
                <span className="px-2 py-1 text-xs bg-primary-100 text-primary-700 rounded">
                  {getLevelName(cls.level)}
                </span>
              </div>
              <div className="space-y-1 text-xs text-gray-600 mb-4">
                <p><span className="font-medium">Filière:</span> {cls.track?.name || 'N/A'}</p>
                <p><span className="font-medium">Dépt:</span> {cls.department?.name || 'N/A'}</p>
                <p><span className="font-medium">Année:</span> {cls.academicYear?.name || 'N/A'}</p>
                <p><span className="font-medium">Étudiants:</span> {cls.students}</p>
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => openModal(cls)}
                  className="flex-1 px-3 py-1 text-xs font-medium text-primary-700 bg-primary-50 rounded hover:bg-primary-100"
                >
                  Modifier
                </button>
                <button 
                  onClick={() => confirmDelete(cls)}
                  className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
                >
                  Suppr.
                </button>
              </div>
            </div>
          ))}
        </div>

        {filteredClasses.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-sm">
              Aucune classe trouvée. {classes.length > 0 ? 'Modifiez les filtres.' : 'Ajoutez une classe pour commencer.'}
            </p>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="flex justify-between items-center p-4 border-b">
                <h3 className="text-lg font-medium text-gray-900">{editingClass ? 'Modifier Classe' : 'Ajouter Classe'}</h3>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">×</button>
              </div>
              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nom de la classe</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Niveau</label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value={1}>1ère année</option>
                    <option value={2}>2ème année</option>
                    <option value={3}>3ème année</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Département</label>
                  <select
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    {departments.map(dept => (
                      <option key={dept._id} value={dept._id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Filière</label>
                  <select
                    name="track"
                    value={formData.track}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                    required
                    disabled={!formData.department}
                  >
                    <option value="">Sélectionner une filière</option>
                    {availableTracks.map(track => (
                      <option key={track._id} value={track._id}>{track.name} ({track.code})</option>
                    ))}
                  </select>
                  {!formData.department && (
                    <p className="text-xs text-gray-500 mt-1">Sélectionnez d'abord un département</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année universitaire</label>
                  <select
                    name="academicYear"
                    value={formData.academicYear}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    {academicYears.map(year => (
                      <option key={year._id} value={year._id}>{year.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Étudiants</label>
                  <input
                    type="number"
                    name="students"
                    value={formData.students}
                    onChange={handleChange}
                    min="0"
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button 
                    type="submit" 
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500"
                  >
                    {editingClass ? 'Modifier' : 'Ajouter'}
                  </button>
                  <button 
                    type="button" 
                    onClick={closeModal} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:ring-2 focus:ring-gray-500"
                  >
                    Annuler
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <DeleteConfirmationModal
          isOpen={deleteConfirm.show}
          onClose={cancelDelete}
          onConfirm={handleConfirmDelete}
          title="Supprimer la classe"
          message={`Êtes-vous sûr de vouloir supprimer la classe ${deleteConfirm.className} ? Toutes les séances associées seront également supprimées.`}
          loading={deleteLoading}
        />
      </div>
    </div>
  );
};

export default Classes;